import { Button, ListboxItem } from "@heroui/react"
import { Plural, Trans, useLingui } from "@lingui/react/macro"
import { Filter, Server } from "lucide-react"
import { useMemo, useState } from "react"

import { count } from "#/toolbar/functions"
import { triggerStyle } from "#/toolbar/style"
import { getHost } from "#/utils"

import { FilterModal, type FilterModalProps } from "../components/FilterModal"
import type { Har } from "../har"

function reverseDNS(host: string) {
  return host.split(".").reverse().join(".")
}

interface HostTypeModalProps
  extends Pick<
    FilterModalProps<[string, number]>,
    | "isOpen"
    | "onOpenChange"
    | "onIncludeChange"
    | "onExcludeChange"
    | "includeValue"
    | "excludeValue"
  > {
  har?: Har
}

function HostTypeModal({ har, ...props }: HostTypeModalProps) {
  const { t } = useLingui()
  const { onExcludeChange, excludeValue } = props
  const counts = useMemo(
    () => count(har?.log.entries, e => getHost(e.request.url)),
    [har]
  )

  const allOptions = useMemo(
    () =>
      Array.from(counts).sort((a, b) => reverseDNS(a[0]).localeCompare(reverseDNS(b[0]))),
    [counts]
  )

  return (
    <FilterModal
      excludeLabel={t`Excluded hosts`}
      filter={([key], query) => key.toLowerCase().includes(query.toLowerCase())}
      includeLabel={t`Included hosts`}
      items={allOptions}
      {...props}
      footerActions={
        <Button
          color="danger"
          css="border-1"
          variant="bordered"
          onPress={() => onExcludeChange(excludeBackground.union(excludeValue))}
        >
          <Trans>Exclude background and telemetry</Trans>
        </Button>
      }
      header={
        <>
          <Filter size={17} />
          <Trans>Host</Trans>
        </>
      }
    >
      {([key, count]) => (
        <ListboxItem key={key} textValue={key}>
          {key}
          {count !== 1 && ` (${count})`}
        </ListboxItem>
      )}
    </FilterModal>
  )
}

function HostTypeButtonLabel({
  includeValue,
  excludeValue,
}: {
  includeValue: Set<string>
  excludeValue: Set<string>
}) {
  if (includeValue.size === 1) {
    const host = includeValue.values().next().value!
    return (
      <>
        <Server size={16} />
        {host}
      </>
    )
  } else if (includeValue.size) {
    return (
      <>
        <Server size={16} />
        <Plural
          one="# host included"
          other="# hosts included"
          value={includeValue.size}
        />
      </>
    )
  } else if (excludeValue.size) {
    return (
      <>
        <Server size={16} />
        <Plural
          one="# host excluded"
          other="# hosts excluded"
          value={excludeValue.size}
        />
      </>
    )
  } else {
    return (
      <>
        <Server size={16} />
        <Trans>Filter by host…</Trans>
      </>
    )
  }
}

export function HostTypeButton({
  har,
  includeValue,
  excludeValue,
  onExcludeChange,
  onIncludeChange,
}: Pick<
  HostTypeModalProps,
  "har" | "includeValue" | "excludeValue" | "onExcludeChange" | "onIncludeChange"
>) {
  const [isOpen, onOpenChange] = useState(false)

  return (
    <>
      <Button
        className={triggerStyle}
        css="text-[14px]"
        size="sm"
        variant="flat"
        onPress={() => onOpenChange(true)}
      >
        <HostTypeButtonLabel excludeValue={excludeValue} includeValue={includeValue} />
      </Button>

      <HostTypeModal
        {...{
          excludeValue,
          har,
          includeValue,
          isOpen,
          onExcludeChange,
          onIncludeChange,
          onOpenChange,
        }}
      />
    </>
  )
}

// spellchecker:disable
const excludeBackground = new Set([
  "analytics.google.com",
  "analytics.tiktok.com",
  "api.segment.io",
  "api2.amplitude.com",
  "bam.nr-data.net",
  "beacons.gcp.gvt2.com",
  "beacons.gvt2.com",
  "browser-intake-datadoghq.com",
  "c.go-mpulse.net",
  "cdn.inspectlet.com",
  "cdn.pendo.io",
  "cdn.segment.com",
  "chat.google.com",
  "clients4.google.com",
  "cm.everesttech.net",
  "collect.analyze.ly",
  "configuration.ls.apple.com",
  "data.pendo.io",
  "datacloud.teamliumiq.com",
  "dev.visualwebsiteoptimizer.com",
  "dpm.demdex.net",
  "eastus-8.in.applicationinsights.azure.com",
  "edge.fullstory.com",
  "enterprise-safebrowsing.googleapis.com",
  "firebaseinstallations.googleapis.com",
  "fonts.gstatic.com",
  "fs.fullstory.com",
  "gateway.facebook.com",
  "hn.inspectlet.com",
  "js-cdn.dynatrace.com",
  "mail.google.com",
  "maxcdn.bootstrapcdn.com",
  "mobile.events.data.microsoft.com",
  "notes-analytics-events.apple.com",
  "ocsp2.apple.com",
  "ops.google.com",
  "optimizationguide-pa.googleapis.com",
  "pancake.apple.com",
  "play.google.com",
  "prod-dynamite-prod-05-us-signaler-pa.clients6.google.com",
  "rs.fullstory.com",
  "rum.browser-intake-datadoghq.com",
  "s.go-mpulse.net",
  "safebrowsing.google.com",
  "se.monetate.net",
  "sentry.io",
  "session-replay.browser-intake-datadoghq.com",
  "signaler-pa.clients6.google.com",
  "siteintercept.qualtrics.com",
  "siteintercept.qualtrics.com",
  "ssl.google-analytics.com",
  "ssl.gstatic.com",
  "stackpath.bootstrapcdn.com",
  "taskassist-pa.clients6.google.com",
  "telemetry.1passwordservices.com",
  "us.telemetry.zoom.us",
  "usea1-001-mssp.sentinelone.net",
  "valid.apple.com",
  "waa-pa.clients6.google.com",
  "westus-0.in.applicationinsights.azure.com",
  "www.gem.com",
  "www.google-analytics.com",
  "www.googletagmanager.com",
  "www.igvita.com",
])
// spellchecker:enable
